import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { useRender } from "@base-ui-components/react/use-render";
import { cva } from "class-variance-authority";
import { cn } from "./lib/utils.js";
/**
 * The filled variants are two layers behind the label: `::before` is the solid
 * fill and the drop shadow, `::after` is the lit top edge and the hover wash.
 *
 * Both are inset by -1px, NOT 0. `inset: 0` on an absolutely positioned
 * pseudo-element resolves against the PADDING box, so on these variants (which
 * all carry a 1px transparent border) it lands 1px inside the button while
 * `rounded-[inherit]` still hands it the border box's radius. Two curves of
 * equal radius on boxes offset by 1px do not nest: they stay parallel down the
 * straights and diverge at every corner, and the element's own 90%-opacity
 * background shows through that ring as a pale rim that looks like a second,
 * wrong border radius.
 *
 * -1px puts the layers back on the border box, where the inherited radius is
 * the correct one. Correcting the radius instead would mean hardcoding
 * `radius - 1`, which breaks the moment a size changes it: `icon` is
 * rounded-xl, the rest rounded-lg.
 */
const depthLayers = "ui:relative ui:isolate ui:before:content-[''] ui:before:absolute ui:before:-inset-px ui:before:-z-10 ui:before:rounded-[inherit] ui:before:shadow-[0_1px_2px_0_rgb(0_0_0/0.05)] ui:after:content-[''] ui:after:absolute ui:after:-inset-px ui:after:-z-10 ui:after:rounded-[inherit] ui:after:shadow-[inset_0_1px_0_0_rgb(255_255_255/0.15)] ui:hover:after:bg-white/10";
const buttonVariants = cva("ui:inline-flex ui:cursor-default ui:items-center ui:justify-center ui:gap-1.5 ui:rounded-lg ui:font-semibold ui:whitespace-nowrap ui:transition-colors ui:focus-visible:border-border-focus ui:focus-visible:shadow-[0_0_0_3px_var(--m-accent-2-surface)] ui:focus-visible:outline-hidden ui:disabled:pointer-events-none ui:disabled:cursor-not-allowed ui:disabled:opacity-50 ui:[&_svg]:pointer-events-none ui:[&_svg]:shrink-0", {
    variants: {
        variant: {
            default: `${depthLayers} ui:border ui:border-transparent ui:bg-[oklch(0.60_0.14_137.4/0.9)] ui:text-fg-on-accent ui:before:bg-accent ui:disabled:bg-fg/12 ui:disabled:text-fg/38 ui:disabled:before:bg-transparent ui:disabled:after:hidden`,
            secondary: `${depthLayers} ui:border ui:border-transparent ui:bg-[oklch(0.32_0.07_235.9/0.9)] ui:text-white ui:dark:text-fg-on-accent ui:before:bg-accent-2 ui:disabled:bg-fg/12 ui:disabled:text-fg/38 ui:disabled:before:bg-transparent ui:disabled:after:hidden`,
            destructive: `${depthLayers} ui:border ui:border-transparent ui:bg-[oklch(0.46_0.16_29.9/0.9)] ui:text-white ui:dark:text-fg-on-accent ui:before:bg-error ui:disabled:bg-fg/12 ui:disabled:text-fg/38 ui:disabled:before:bg-transparent ui:disabled:after:hidden`,
            outline: "ui:border-[1.5px] ui:border-border-strong ui:bg-transparent ui:text-accent-2 ui:transition-colors ui:hover:border-accent-2 ui:hover:bg-accent-2-surface",
            subtle: "ui:border ui:border-transparent ui:bg-surface-0 ui:text-fg ui:transition-colors ui:hover:bg-surface-0/90",
            ghost: "ui:border ui:border-transparent ui:bg-transparent ui:text-fg-muted ui:transition-colors ui:hover:bg-surface-0",
            /**
             * The action-launch chip (DESIGN-GUIDELINES §12): a faint wash and rim
             * that say "this opens something elsewhere" without saying it loudly.
             *
             * For a control that repeats — a row of a list, a field in a record
             * grid — where a filled button would out-shout the content it serves
             * and a bare link would read as more text. Shape carries the
             * affordance, so the label can stay muted and still be unmistakably
             * pressable. Visible at rest and deepening on hover, per that spec: an
             * affordance that only exists on hover is not an affordance.
             *
             * Neutral `fg` tints rather than a surface step, because this hosts on
             * whatever the list or grid is painted on and light mode's surface ramp
             * is compressed enough that a step is a no-op on half of them. A rim
             * on the existing transparent border rather than §12's `ring`, which
             * would fight the base's focus-visible shadow for the same property.
             */
            trigger: "ui:cursor-pointer ui:rounded-md ui:border ui:border-fg/10 ui:bg-fg/3 ui:font-normal ui:text-fg-muted ui:transition-colors ui:hover:border-fg/20 ui:hover:bg-fg/7 ui:hover:text-fg",
            link: "ui:cursor-pointer ui:border-none ui:bg-transparent ui:p-0 ui:font-medium ui:text-accent-2 ui:underline ui:decoration-current/40 ui:underline-offset-2 ui:transition-colors ui:hover:decoration-current",
        },
        size: {
            default: "ui:px-[calc(--spacing(3.5)-1px)] ui:py-[calc(--spacing(2.5)-1px)] ui:text-base/6 ui:sm:px-[calc(--spacing(3)-1px)] ui:sm:py-[calc(--spacing(1.5)-1px)] ui:sm:text-sm/6 ui:[&_svg]:size-4",
            sm: "ui:px-[calc(--spacing(3)-1px)] ui:py-[calc(--spacing(1.5)-1px)] ui:text-sm/6 ui:sm:px-[calc(--spacing(2.5)-1px)] ui:sm:py-[calc(--spacing(1)-1px)] ui:sm:text-[0.8125rem]/6 ui:[&_svg]:size-3.5",
            lg: "ui:px-[calc(--spacing(4)-1px)] ui:py-[calc(--spacing(3)-1px)] ui:text-base/6 ui:sm:px-[calc(--spacing(3.5)-1px)] ui:sm:py-[calc(--spacing(2.5)-1px)] ui:sm:text-[0.9375rem]/6 ui:[&_svg]:size-5",
            /**
             * 24px tall, for a control living inside a list row rather than beside
             * one. `sm` is 28px before its border and carries 13px text, which is
             * the size of the row's own title — a button that matches the thing it
             * is subordinate to competes with it. This is deliberately the size of
             * a row's trailing metadata, and it does not grow at the `sm:`
             * breakpoint because the row it sits in does not either.
             *
             * Its glyph is 12px and sits 4px from the label, tighter than every
             * other size. At this scale a button's icon is a suffix rather than an
             * identifier — the word beside it has already said what it does, and
             * the mark only adds the shade of meaning the word cannot, so it can
             * be small and close. The chips this sits among run the other way:
             * their glyph IS what names the fact, so it is bigger and given room.
             */
            xs: "ui:gap-1 ui:rounded-md ui:px-[calc(--spacing(1.5)-1px)] ui:py-[calc(--spacing(0.5)-1px)] ui:text-[0.72rem]/5 ui:[&_svg]:size-3",
            icon: "ui:size-9 ui:rounded-xl ui:[&_svg]:size-4",
            "icon-xs": "ui:size-5 ui:rounded-md ui:[&_svg]:size-3.5",
            "icon-sm": "ui:size-7 ui:rounded-xl ui:[&_svg]:size-3.5",
            "icon-lg": "ui:size-11 ui:rounded-xl ui:[&_svg]:size-5",
        },
    },
    compoundVariants: [
        {
            variant: "ghost",
            size: ["icon", "icon-xs", "icon-sm", "icon-lg"],
            className: "ui:border ui:border-transparent ui:bg-transparent ui:text-fg-muted ui:transition-colors ui:hover:bg-surface-0",
        },
        {
            variant: "outline",
            size: ["icon", "icon-xs", "icon-sm", "icon-lg"],
            className: "ui:border ui:border-border ui:bg-surface-2 ui:text-fg-muted ui:transition-colors ui:hover:bg-surface-0",
        },
    ],
    defaultVariants: {
        variant: "default",
        size: "default",
    },
});
const Button = React.forwardRef(({ className, variant, size, asChild = false, children, ...props }, ref) => {
    // With `asChild`, our children prop is the element we compose onto, so it
    // is never forwarded — the child keeps its own children.
    const child = asChild && React.isValidElement(children)
        ? children
        : null;
    return useRender({
        render: child ?? _jsx("button", {}),
        ref,
        props: {
            className: cn(buttonVariants({ variant, size, className })),
            ...props,
            ...(child ? null : { children }),
        },
    });
});
Button.displayName = "Button";
export { Button, buttonVariants };
